'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import {
  GoogleMap,
  Marker,
  Polyline,
  useLoadScript,
} from '@react-google-maps/api'
import { RouteLocation } from 'lib/types/maps'
import {
  getValidLocations,
  isValidCoordinate,
} from 'lib/utils/trackingMapUtils'

const containerStyle = {
  width: '100%',
  height: '400px',
}

const defaultCenter = {
  lat: 39.833333,
  lng: -98.583333,
}

const GOOGLE_MAPS_API_KEY =
  process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_CLIENT_KEY || ''

const zoom = 5

const lightGrayStyle = [
  {
    featureType: 'all',
    elementType: 'all',
    stylers: [{ saturation: -80 }, { lightness: 10 }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ visibility: 'simplified' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#e0e0e0' }],
  },
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [{ color: '#f5f5f5' }],
  },
]

const solidRouteLine = {
  strokeColor: '#22c55e',
  strokeOpacity: 1.0,
  strokeWeight: 3,
  geodesic: true,
}

const dottedRouteLine = {
  strokeColor: '#22c55e',
  strokeOpacity: 0,
  strokeWeight: 3,
  geodesic: true,
  icons: [
    {
      icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 4 },
      offset: '0',
      repeat: '20px',
    },
  ],
}

const errorRouteLine = {
  strokeColor: '#ff0000',
  strokeOpacity: 0,
  strokeWeight: 3,
  geodesic: true,
  icons: [
    {
      icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 4 },
      offset: '0',
      repeat: '20px',
    },
  ],
}

const options = {
  disableDefaultUI: true,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  styles: lightGrayStyle,
}

interface OrderTrackingMapProps {
  routeLocations?: RouteLocation[]
  showRoute?: boolean
}

export default function OrderTrackingMap({
  routeLocations = [],
  showRoute = true,
}: OrderTrackingMapProps) {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  })

  const [geocodedLocations, setGeocodedLocations] = useState<RouteLocation[]>(
    [],
  )
  const [mapCenter, setMapCenter] = useState(defaultCenter)
  const [routePath, setRoutePath] = useState<google.maps.LatLng[]>([])
  const [isGeocoding, setIsGeocoding] = useState(false)
  const [routeLineOptions, setRouteLineOptions] = useState<Record<
    string,
    any
  > | null>(null)

  const mapRef = useRef<google.maps.Map | null>(null)
  const markersRef = useRef<{ [key: string]: google.maps.Marker }>({})
  const geocodingInProgressRef = useRef(false)

  // Determine route line style based on status
  useEffect(() => {
    if (routeLocations?.length > 0) {
      const loc = routeLocations[0]
      if (loc.status === 'cancelled' || loc.status === 'rejected') {
        setRouteLineOptions(errorRouteLine)
      } else if (loc.status !== 'delivered') {
        setRouteLineOptions(dottedRouteLine)
      } else {
        setRouteLineOptions(solidRouteLine)
      }
    }
  }, [routeLocations])

  // Geocode locations that need it
  const geocodeLocations = useCallback(async () => {
    if (
      !window.google?.maps ||
      routeLocations.length === 0 ||
      geocodingInProgressRef.current
    ) {
      return
    }

    geocodingInProgressRef.current = true
    setIsGeocoding(true)

    const geocoder = new window.google.maps.Geocoder()
    const geocodedResults: RouteLocation[] = []

    try {
      for (const loc of routeLocations) {
        if (isValidCoordinate(loc.latitude, loc.longitude)) {
          geocodedResults.push(loc)
        } else if (loc.address?.trim()) {
          try {
            const results = await new Promise<google.maps.GeocoderResult[]>(
              (resolve, reject) => {
                geocoder.geocode(
                  { address: loc.address!.trim() },
                  (results, status) => {
                    if (status === 'OK' && results && results.length > 0) {
                      resolve(results)
                    } else {
                      reject(new Error(`Geocoding failed: ${status}`))
                    }
                  },
                )
              },
            )

            const location = results[0].geometry.location
            const lat = location.lat()
            const lng = location.lng()

            if (isValidCoordinate(lat, lng)) {
              const geocodedLoc = {
                ...loc,
                latitude: lat,
                longitude: lng,
              }
              geocodedResults.push(geocodedLoc)
            } else {
              geocodedResults.push(loc)
            }
          } catch (error) {
            console.warn(`Geocoding failed for ${loc.address}:`, error)
            geocodedResults.push(loc)
          }
        } else {
          geocodedResults.push(loc)
        }

        // Small delay to avoid rate limiting
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      setGeocodedLocations(geocodedResults)

      // Set up the map bounds with valid coordinates
      const validLocations = getValidLocations(geocodedResults)

      if (validLocations.length > 0 && mapRef.current) {
        const bounds = new window.google.maps.LatLngBounds()

        validLocations.forEach((loc) => {
          bounds.extend({ lat: loc.latitude!, lng: loc.longitude! })
        })

        try {
          mapRef.current.fitBounds(bounds)
          const center = bounds.getCenter()

          if (center) {
            const centerLat = center.lat()
            const centerLng = center.lng()

            if (isValidCoordinate(centerLat, centerLng)) {
              setMapCenter({ lat: centerLat, lng: centerLng })
            }
          }
        } catch (boundsError) {
          console.warn('Failed to set bounds:', boundsError)
        }
      }
    } catch (error) {
      console.warn('Geocoding process failed:', error)
    } finally {
      setIsGeocoding(false)
      geocodingInProgressRef.current = false
    }
  }, [routeLocations])

  // Create route path from geocoded locations
  useEffect(() => {
    if (!showRoute || !isLoaded || geocodedLocations.length < 2) {
      setRoutePath([])
      return
    }

    const validLocations = getValidLocations(geocodedLocations)

    if (validLocations.length < 2) {
      setRoutePath([])
      return
    }

    try {
      // Sort by type: origin -> waypoints -> destination
      const sortedLocations = [...validLocations].sort((a, b) => {
        const typeOrder: Record<RouteLocation['type'], number> = {
          origin: 0,
          waypoint: 1,
          destination: 2,
        }
        return typeOrder[a.type] - typeOrder[b.type]
      })

      const newPath = sortedLocations
        .map((loc) => {
          try {
            return new google.maps.LatLng(loc.latitude!, loc.longitude!)
          } catch (latLngError) {
            console.error(
              `Failed to create LatLng for ${loc.name}:`,
              latLngError,
            )
            return null
          }
        })
        .filter((latLng): latLng is google.maps.LatLng => latLng !== null)

      setRoutePath(newPath)
    } catch (error) {
      console.warn('Failed to create route path:', error)
      setRoutePath([])
    }
  }, [geocodedLocations, showRoute, isLoaded])

  // Start geocoding when locations change
  useEffect(() => {
    if (isLoaded && routeLocations.length > 0) {
      geocodeLocations()
    }
  }, [isLoaded, routeLocations, geocodeLocations])

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map
  }, [])

  const onUnmount = useCallback(() => {
    mapRef.current = null
    // Clean up markers
    Object.values(markersRef.current).forEach((marker) => {
      try {
        marker.setMap(null)
      } catch (error) {
        console.warn('Error cleaning up marker:', error)
      }
    })
    markersRef.current = {}
  }, [])

  // Get marker icon based on location type and status
  const getMarkerIcon = useCallback(
    (status: string = '') => {
      if (!isLoaded || !window.google?.maps) return undefined
      const markerColor =
        status === 'cancelled' || status === 'rejected' ? '#ff0000' : '#22c55e'
      try {
        return {
          url:
            'data:image/svg+xml;base64,' +
            btoa(
              '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
                `<circle cx="12" cy="12" r="8" fill="${markerColor}" stroke="#ffffff" stroke-width="2"/>` +
                '<circle cx="12" cy="12" r="3" fill="#ffffff"/>' +
                '</svg>',
            ),
          scaledSize: new window.google.maps.Size(24, 24),
          anchor: new window.google.maps.Point(12, 12),
          origin: new window.google.maps.Point(0, 0),
        }
      } catch (error) {
        console.warn('Failed to create marker icon:', error)
        return undefined
      }
    },
    [isLoaded],
  )

  const handleMarkerLoad = useCallback(
    (marker: google.maps.Marker, locationId: string) => {
      markersRef.current[locationId] = marker
    },
    [],
  )

  const handleMarkerUnmount = useCallback((locationId: string) => {
    delete markersRef.current[locationId]
  }, [])

  // Handle loading error
  if (loadError) {
    return <div>Error loading Google Maps</div>
  }

  if (!isLoaded) {
    return <div>Loading map...</div>
  }

  if (!GOOGLE_MAPS_API_KEY) {
    return <div>Google Maps API key is required</div>
  }

  const validGeocodedLocations = getValidLocations(geocodedLocations)

  return (
    <>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapCenter}
        zoom={zoom}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={options}
      >
        {/* Route Line */}
        {showRoute && routePath.length > 1 && routeLineOptions && (
          <Polyline path={routePath} options={routeLineOptions} />
        )}

        {/* Route Location Markers */}
        {validGeocodedLocations.map((loc, index) => {
          const markerId = loc.id || `${loc.latitude}-${loc.longitude}-${index}`

          return (
            <Marker
              key={`route-${markerId}`}
              position={{ lat: loc.latitude!, lng: loc.longitude! }}
              onLoad={(marker) => handleMarkerLoad(marker, loc.id)}
              onUnmount={() => handleMarkerUnmount(loc.id)}
              icon={getMarkerIcon(loc.status)}
              title={loc.name}
            />
          )
        })}
      </GoogleMap>

      {/* Status indicator */}
      {isGeocoding && (
        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">Small</h3>
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-200 border-t-purple-500 mx-auto"></div>
          <code className="text-xs mt-4 block text-gray-600">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-200 border-t-purple-500"></div>
          </code>
        </div>
      )}
    </>
  )
}
